import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

import { supabase } from '@/integrations/supabase/client';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface AdminNotification {
  id: string;
  event_type: string;
  actor_name: string;
  actor_user_id: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
  is_read: boolean;
}

function formatNotificationText(n: AdminNotification): string {
  const suffix = n.actor_user_id ? '' : ' (deleted user)';
  if (n.event_type === 'new_registration') {
    return `🆕 ${n.actor_name} registered${suffix}`;
  }
  if (n.event_type === 'first_scout') {
    return `⚽ ${n.actor_name} completed their first scout${suffix}`;
  }
  if (n.event_type === 'new_comment') {
    return `💬 ${n.actor_name} commented${suffix}`;
  }
  return `${n.event_type}: ${n.actor_name}${suffix}`;
}

export function AdminNotificationBell() {
  const navigate = useNavigate();
  const { isAdmin } = useIsAdmin();
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    const { data } = await (supabase as any).rpc('get_admin_notifications', { p_limit: 30 });
    if (!data) return;
    const rows = (data as AdminNotification[]).sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    setNotifications(rows);
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60_000);
    return () => clearInterval(interval);
  }, [isAdmin, fetchNotifications]);

  useEffect(() => {
    if (!open || !isAdmin) return;
    (async () => {
      await (supabase as any).rpc('mark_admin_notifications_read', { p_ids: null });
      await fetchNotifications();
    })();
  }, [open, isAdmin, fetchNotifications]);

  if (!isAdmin) return null;

  const handleClick = (n: AdminNotification) => {
    if (n.event_type === 'new_comment' && n.payload) {
      const playerId = n.payload.player_id as string | undefined;
      const commentId = n.payload.comment_id as string | undefined;
      if (playerId) {
        setOpen(false);
        navigate(`/player/${playerId}${commentId ? `#comment-${commentId}` : ''}`);
      }
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-muted-foreground hover:text-foreground"
          aria-label="Admin notifications"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-4 w-4 items-center justify-center rounded-full p-0 text-[10px]"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-auto">
        {notifications.length === 0 ? (
          <DropdownMenuItem disabled className="text-muted-foreground">
            No notifications
          </DropdownMenuItem>
        ) : (
          notifications.map((n) => {
            const clickable = n.event_type === 'new_comment' && !!n.payload?.player_id;
            return (
              <DropdownMenuItem
                key={n.id}
                onClick={() => handleClick(n)}
                className={cn(
                  'flex flex-col items-start gap-0.5 px-3 py-2',
                  clickable ? 'cursor-pointer' : 'cursor-default',
                  !n.is_read && 'bg-muted',
                )}
              >
                <span className="text-sm">{formatNotificationText(n)}</span>
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                </span>
              </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
